import React, { FC, useMemo, useState, useCallback } from 'react';
import { Select, Input } from 'antd';
import { useSelector } from 'react-redux';
import { ReactComponent as ClockIcon } from '@blink/assets/src/icons/clock.svg';
import { ReactComponent as InfoIcon } from '@blink/assets/src/icons/infoCircle.svg';
import { ReactComponent as MagnifyingGlassIcon } from '@blink/assets/src/icons/magnifyingGlass.svg';
import { UNSUPPORTED_NETWORK_ID } from '@blink/components/src/constants/global';

import { VaultDepositItemRow } from '../VaultDepositItemRow';
import { useVaultDepositStyle } from './VaultDeposit.style';

import { Store } from '@/shared/store';
import { ROLE, Vault } from '@/shared/api/vault';
import { EmptyBanner } from '@/widgets/Banners/EmptyBanner';
import { SkeletonRow, TooltipWrapper } from '@/shared/ui';
import { useHasVaultsError } from '@/shared/hooks/useErrors';
import { ServerError } from '@/widgets/ServerError';
import { getVaultsAction } from '@/shared/store/vaults/actions';

const { Option } = Select;

const Filter = {
    ALL: 'All',
    ACTIVE: 'Active',
    INACTIVE: 'Inactive',
};

export const VaultDeposit: FC = () => {
    const classes = useVaultDepositStyle();

    const { vaults, vaultsLoading, network } = useSelector((state: Store) => ({
        vaults: state.vaults.vaults,
        vaultsLoading: state.vaults.vaultsLoading,
        network: state.wallets.network,
    }));

    const hasError = useHasVaultsError();

    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState(Filter.ALL);

    const investVaults = useMemo(
        () => vaults.filter((vault) => vault.role === ROLE.INVESTOR),
        [vaults],
    );

    const filteredVaults = useMemo(() => {
        return investVaults.filter((vault: Vault) => {
            const matchesSearch =
                vault.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                vault.address.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesStatus =
                statusFilter === Filter.ALL ||
                (statusFilter === Filter.ACTIVE && vault.suspended === true) ||
                (statusFilter === Filter.INACTIVE && vault.suspended === false);

            return matchesSearch && matchesStatus;
        });
    }, [investVaults, searchTerm, statusFilter]);

    const isEmpty = useMemo(
        () =>
            (!vaultsLoading && (vaults.length === 0 || vaults[0].address === '')) ||
            filteredVaults.length === 0 ||
            network === UNSUPPORTED_NETWORK_ID,
        [vaults, vaultsLoading, filteredVaults, network],
    );

    const renderSkeletons = () => (
        <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
        </>
    );

    const renderVaultRows = () =>
        filteredVaults.map((vault, index) => (
            <VaultDepositItemRow key={index} itemIndex={index} allItems={filteredVaults} />
        ));

    const getContent = useCallback(() => {
        if (hasError) {
            return <ServerError action={getVaultsAction} />;
        }

        if (isEmpty) {
            return (
                <div>
                    <EmptyBanner
                        customTitle='You don’t have any vaults yet.'
                        customSubtitle='Please check back later or contact support if you need help with vaults.'
                        icon={ClockIcon}
                    />
                </div>
            );
        }

        return (
            <div className='overflow-x-auto [&_th]:font-medium'>
                <table className='border-collapse w-full bg-primary border border-opaque min-w-[950px]'>
                    <thead>
                        <tr
                            className='
                                border-b border-opaque h-[48px]
                                [&_span]:font-semibold
                                [&_th]:font-semibold [&_th]:text-left [&_th]:text-primary [&_th]:w-[20%] [&_th]:bg-background-secondary [&_th]:px-5 [&_th]:py-[14px]
                                [&_th:last-child]:text-right [&_td:last-child]:pr-7 [&_td:first-child]:pl-6'
                        >
                            <th>Vault Name</th>
                            <th>Asset</th>
                            <th>Total Supply</th>
                            <th>
                                <div className={classes.apyTitle}>
                                    <TooltipWrapper
                                        text='Net APY is the total annual yield from your deposit, including interest and rewards.'
                                        icon={<InfoIcon />}
                                    />
                                    <span>Net APY</span>
                                </div>
                            </th>
                            <th>Curator</th>
                            <th className={classes.buttons} />
                        </tr>
                    </thead>
                    <tbody>{vaultsLoading ? renderSkeletons() : renderVaultRows()}</tbody>
                </table>
            </div>
        );
    }, [hasError, isEmpty, vaultsLoading, filteredVaults, classes]);

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.title}>
                    <span>Deposit in a vault</span>
                    <TooltipWrapper
                        text='Depositing assets into a vault curated by risk experts generates yield. Each vault has a unique strategy and risk profile, determined by the curator. Deposits are allocated to Morpho Markets, and earn yield from borrowers.'
                        icon={<InfoIcon />}
                    />
                </div>
                <div className={classes.container}>
                    <Input
                        type='text'
                        placeholder='Search or paste address'
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className={classes.searchInput}
                        suffix={<MagnifyingGlassIcon style={{ marginLeft: '8px' }} />}
                    />
                    <Select
                        defaultValue={Filter.ALL}
                        value={statusFilter}
                        onChange={(value) => setStatusFilter(value)}
                        className={classes.selectInput}
                    >
                        <Option value={Filter.ALL}>{Filter.ALL}</Option>
                        <Option value={Filter.ACTIVE}>{Filter.ACTIVE}</Option>
                        <Option value={Filter.INACTIVE}>{Filter.INACTIVE}</Option>
                    </Select>
                </div>
            </div>

            {getContent()}
        </div>
    );
};
